import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Button,
  IconButton,
  Divider,
} from "@mui/material";
import { useParams } from "react-router-dom";
import axios from "axios";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ShareIcon from "@mui/icons-material/Share";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CallIcon from "@mui/icons-material/Call";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";

// Import your bottom navbar
import CustomBottomNavbar from "../../Users/Post Module/FormsBottomNavbar";

const BANK_API = "https://landnest.net:81/auction-property/";

const AuctionPropertyCardDynamic = () => {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const res = await axios.get(BANK_API);
        const found = res.data.find((item) => String(item.bankprop_id) === id);
        setProperty(found);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching property:", err);
        setLoading(false);
      }
    };
    fetchProperty();
  }, [id]);

  const formatPropertyId = (id) => {
    return `BAP${String(id).padStart(4, '0')}`;
  };

  const handleCallButtonClick = () => {
    if (property?.bank_contact_details) {
      // Copy contact details to clipboard
      navigator.clipboard.writeText(property.bank_contact_details)
        .then(() => {
          // Show window alert
          window.alert(`Contact number copied: ${property.bank_contact_details}\n\nProperty ID: ${formatPropertyId(property.bankprop_id)}`);
        })
        .catch((err) => {
          console.error("Failed to copy: ", err);
          window.alert("Failed to copy contact number");
        });
    } else {
      window.alert("Contact number not available");
    }
  };

  if (loading) return <Typography sx={{ p: 2 }}>Loading...</Typography>;
  if (!property) return <Typography sx={{ p: 2 }}>Property not found</Typography>;

  return (
    <Box sx={{ pb: 0 }}> {/* Top padding for gap and bottom padding for navbar */}
      <Card sx={{ maxWidth: 420, boxShadow: 4, mx: "auto" }}>
        {/* Header */}
        <Box
          sx={{
            backgroundColor: "black",
            color: "#fff",
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
          }}
        >
          <IconButton
            size="small"
            sx={{ color: "#fff" }}
            onClick={() => window.history.back()}
          >
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>

          <Typography fontWeight={600} flexGrow={1}>
            Auction Property
          </Typography>

          <Box textAlign="right">
            <Box textAlign="right">
              <Typography variant="caption">
                ID: {formatPropertyId(property.bankprop_id)}
              </Typography>
            </Box>

            <Typography variant="caption" display="block">
              Date: {new Date(property.created_at).toLocaleDateString()}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: 2 }}>
          {/* Image */}
          <Box position="relative">
            <CardMedia
              component="img"
              height="190"
              image={property.bank_pro_doc[0] || "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2"}
              alt="property"
              sx={{ borderRadius: 2 }}
            />

            <Box position="absolute" top={8} right={8} display="flex" gap={1}>
              <IconButton sx={{ bgcolor: "#fff" }}>
                <ShareIcon fontSize="small" />
              </IconButton>
              <IconButton sx={{ bgcolor: "#fff" }}>
                <FavoriteBorderIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <CardContent sx={{ p: 0, pt: 2 }}>
            {/* Reserve Price */}
            <Box
              sx={{
                backgroundColor: "#fff1f0",
                p: 2,
                borderRadius: 2,
                mb: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Reserve Price
              </Typography>
              <Typography variant="h6" color="error" fontWeight={700}>
                ₹{property.reserve_price}
              </Typography>
            </Box>

            {/* Property Info */}
            <Typography variant="body2">
              <b>Property type:</b> {property.property_type}
            </Typography>

            <Typography variant="body2" mt={1}>
              <b>City/Town:</b> {property.city_town} / {property.area_town}
            </Typography>

            <Box display="flex" alignItems="center" mt={1}>
              <LocationOnIcon fontSize="small" color="primary" />
              <Typography variant="body2" ml={0.5}>
                Location
              </Typography>
            </Box>

            <Typography variant="body2" mt={1}>
              <b>Area:</b> {property.area} {property.units}
            </Typography>

            {/* Details Box */}
            <Box
              sx={{
                backgroundColor: "#f9f9f9",
                p: 2,
                borderRadius: 2,
                mt: 2,
              }}
            >
              <InfoRow label="EMD Amount" value={`₹${property.emd_amount}`} />
              <InfoRow label="Bid Increment" value={`₹${property.bid_increment}`} />
              <InfoRow label="EMD Submission" value={new Date(property.emd_submission).toLocaleString()} />
              <InfoRow label="Auction Start" value={new Date(property.auction_start_datetime).toLocaleString()} />
              <InfoRow label="Auction End" value={new Date(property.auction_end_datetime).toLocaleString()} />
              <InfoRow label="Possession" value={`${property.possession_status} / ${property.possession}`} />
            </Box>

            {/* Download Button */}
            <Button
              fullWidth
              variant="contained"
              sx={{
                mt: 2,
                backgroundColor: "#2f3437",
                textTransform: "none",
                borderRadius: 2,
              }}
            >
              Download Auction File
            </Button>

            <Divider sx={{ my: 2 }} />

            {/* Bank Info */}
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography fontWeight={600}>{property.bank_name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Contact: {property.bank_contact_details}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={<CallIcon />}
                sx={{ textTransform: "none" }}
                onClick={handleCallButtonClick}
              >
                Call
              </Button>
            </Box>

            {/* Description */}
            <Box
              sx={{
                border: "1px solid #ddd",
                borderRadius: 2,
                mt: 2,
                p: 1,
                minHeight: 80,
              }}
            >
              <Typography variant="body2">{property.description}</Typography>
            </Box>
          </CardContent>
        </Box>
      </Card>

      {/* ================= Fixed Bottom Navbar ================= */}
      <Box sx={{ position: "fixed", bottom: 0, left: 0, right: 0 }}>
        <CustomBottomNavbar />
      </Box>
    </Box>
  );
};

/* Reusable Row */
const InfoRow = ({ label, value }) => (
  <Box display="flex" justifyContent="space-between" mb={1}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value}
    </Typography>
  </Box>
);

export default AuctionPropertyCardDynamic;